import type { ReactNode } from 'react';
import { OctagonAlert, Clock, ShieldOff, WifiOff, Trash2, AlertCircle, type LucideIcon } from 'lucide-react';
import { Card } from './Card';

export type ErrorKind = 'timeout' | 'denied' | 'forbidden' | 'offline' | 'revoked' | 'codec' | 'generic';

interface ErrorPanelProps {
  kind: ErrorKind;
  message: string;
  action?: ReactNode;
}

const icons: Record<ErrorKind, LucideIcon> = {
  timeout:   Clock,
  denied:    ShieldOff,
  forbidden: ShieldOff,
  offline:   WifiOff,
  revoked:   Trash2,
  codec:     AlertCircle,
  generic:   OctagonAlert,
};

const titles: Record<ErrorKind, string> = {
  timeout:   'No response',
  denied:    'Connection denied',
  forbidden: 'Not allowed',
  offline:   'Host is offline',
  revoked:   'Host revoked',
  codec:     'Codec unavailable',
  generic:   'Connection failed',
};

export function ErrorPanel({ kind, message, action }: ErrorPanelProps) {
  const Icon = icons[kind];
  return (
    <Card variant="elevated" className="w-full max-w-md p-8 text-center">
      <div className="mx-auto mb-4 grid h-12 w-12 place-items-center rounded-full border border-border-subtle bg-bg-overlay">
        <Icon className="h-5 w-5 text-danger" aria-hidden />
      </div>
      <h1 className="text-lg font-semibold text-text-primary">{titles[kind]}</h1>
      <p className="mt-2 text-sm leading-relaxed text-text-secondary">{message}</p>
      {action && <div className="mt-6 flex justify-center">{action}</div>}
    </Card>
  );
}
